/**
 * Resolved buff state (TRD-08 §3).
 *
 * TRD-01 owns the shape and the reset discipline; TRD-08 owns what writes into it.
 *
 * The rule that makes synergies debuggable: mods are recomputed FROM SCRATCH on every
 * dirty recompute, never incrementally adjusted (TRD-08 R-08-05). Every "I sold the tower
 * but kept the buff" bug in this genre comes from incremental adjustment, and they are
 * miserable to track down because the wrong state is indistinguishable from correct state
 * until someone does the arithmetic by hand.
 */
import type { SynergyEffect } from './effects.js';

export interface TowerMods {
  flatDamage: number;
  damageMultiplier: number;
  attackSpeedMultiplier: number;
  /** Additive, in tiles. */
  rangeBonus: number;
  /** Multiplicative. Marksman-2 is "+15% range", which flat tiles cannot express. */
  rangeMultiplier: number;
  critChance: number;
  critMultiplier: number;
  armorPierce: number;
  /** Fraction of the target's max HP added to each hit — Void's identity. */
  percentMaxHpDamage: number;
  chainCount: number;
  pierceCount: number;
  onFireHooks: SynergyEffect[];
  onHitHooks: SynergyEffect[];
  onKillHooks: SynergyEffect[];
}

export interface RunMods {
  goldPerWave: number;
  interestCap: number;
  rerollCostMultiplier: number;
  bonusPlacements: number;
  bonusCoreHp: number;
  goldDropChance: number;
  /**
   * Warden-6: every leak costs exactly this much Core HP, whatever leaked. 0 means no
   * override and the enemy's own `leakDamage` applies.
   *
   * A sentinel rather than `number | null` because `NumericRunMod` is derived as "the
   * keys of RunMods whose type is number" — so a nullable field is invisible to the
   * `runStat` effect, and Warden-6 could not be authored at all. It shipped as
   * `bonusCoreHp +0`: a capstone that was, literally, nothing. Zero is safe as the
   * sentinel because the content schema already floors `leakDamage` at 1.
   */
  leakDamageOverride: number;
}

export function createTowerMods(): TowerMods {
  return {
    flatDamage: 0,
    damageMultiplier: 1,
    attackSpeedMultiplier: 1,
    rangeBonus: 0,
    rangeMultiplier: 1,
    critChance: 0,
    critMultiplier: 2,
    armorPierce: 0,
    percentMaxHpDamage: 0,
    chainCount: 0,
    pierceCount: 0,
    onFireHooks: [],
    onHitHooks: [],
    onKillHooks: [],
  };
}

/** Reset to identity in place. Multipliers go to 1, everything else to 0/empty. */
export function resetTowerMods(m: TowerMods): void {
  m.flatDamage = 0;
  m.damageMultiplier = 1;
  m.attackSpeedMultiplier = 1;
  m.rangeBonus = 0;
  m.rangeMultiplier = 1;
  m.critChance = 0;
  m.critMultiplier = 2;
  m.armorPierce = 0;
  m.percentMaxHpDamage = 0;
  m.chainCount = 0;
  m.pierceCount = 0;
  m.onFireHooks.length = 0;
  m.onHitHooks.length = 0;
  m.onKillHooks.length = 0;
}

export function createRunMods(): RunMods {
  return {
    goldPerWave: 0,
    interestCap: 5,
    rerollCostMultiplier: 1,
    bonusPlacements: 0,
    bonusCoreHp: 0,
    goldDropChance: 0,
    leakDamageOverride: 0,
  };
}

export function resetRunMods(m: RunMods): void {
  m.goldPerWave = 0;
  m.interestCap = 5;
  m.rerollCostMultiplier = 1;
  m.bonusPlacements = 0;
  m.bonusCoreHp = 0;
  m.goldDropChance = 0;
  m.leakDamageOverride = 0;
}
